import { IColumn, IGroup } from '@fluentui/react';

import { ICustomGroupConfig } from './DataTable';

function groupBy<T>(arr: T[], keyFn: (item: T) => string) {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyFn(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

export function buildHierarchicalGroups(
  items: any[],
  groupByKeys: string[],
  columns: IColumn[],
  customTitles?: Record<string, string>
): { groups: IGroup[]; groupedItems: any[] } {
  if (groupByKeys.length === 0) {
    return { groups: [], groupedItems: items };
  }

  const fieldNameToDisplayName = columns.reduce((acc, column) => {
    if (column.fieldName) {
      acc[column.fieldName] = column.name;
    }
    return acc;
  }, {} as Record<string, string>);

  const finalDisplayNames = { ...fieldNameToDisplayName, ...customTitles };

  const groupedItems: any[] = [];
  let globalIndex = 0;

  function createGroupsRecursively(currentItems: any[], keyIndex: number, level: number, parentKey = ''): IGroup[] {
    if (keyIndex >= groupByKeys.length) {
      groupedItems.push(...currentItems);
      return [];
    }

    const currentKey = groupByKeys[keyIndex];
    const currentKeyDisplayName = finalDisplayNames[currentKey] || currentKey;
    const grouped = groupBy(currentItems, (item) => String(item[currentKey] || 'Unknown'));
    const currentGroups: IGroup[] = [];

    for (const [groupValue, groupItems] of Object.entries(grouped)) {
      const groupKey = parentKey ? `${parentKey}-${groupValue}` : groupValue;
      const startIndex = globalIndex;

      const group: IGroup = {
        key: groupKey,
        name: `${currentKeyDisplayName}: ${groupValue}`,
        startIndex,
        count: groupItems.length,
        level,
        isCollapsed: true,
        children: [],
      };

      const childGroups = createGroupsRecursively(groupItems, keyIndex + 1, level + 1, groupKey);

      if (childGroups.length > 0) {
        group.children = childGroups;
      } else {
        globalIndex += groupItems.length;
      }

      currentGroups.push(group);
    }

    return currentGroups;
  }

  const resultGroups = createGroupsRecursively(items, 0, 0);
  return { groups: resultGroups, groupedItems };
}

export function buildHierarchicalGroupsWithCustomTitles(
  items: any[],
  customGroupConfig: ICustomGroupConfig[]
): { groups: IGroup[]; groupedItems: any[] } {
  if (customGroupConfig.length === 0) {
    return { groups: [], groupedItems: items };
  }

  const groupedItems: any[] = [];
  let globalIndex = 0;

  function createGroupsRecursively(currentItems: any[], configIndex: number, level: number, parentKey = ''): IGroup[] {
    if (configIndex >= customGroupConfig.length) {
      groupedItems.push(...currentItems);
      return [];
    }

    const config = customGroupConfig[configIndex];
    const grouped = groupBy(currentItems, (item) => String(item[config.fieldName] || 'Unknown'));
    const currentGroups: IGroup[] = [];

    for (const [groupValue, groupItems] of Object.entries(grouped)) {
      const groupKey = parentKey ? `${parentKey}-${groupValue}` : groupValue;
      const startIndex = globalIndex;

      // Use custom title formatter
      const customTitle = config.titleFormatter(groupValue, groupItems, parentKey);

      const group: IGroup = {
        key: groupKey,
        name: customTitle,
        startIndex,
        count: groupItems.length,
        level,
        isCollapsed: config.isCollapsed ?? true,
        children: [],
      };

      const childGroups = createGroupsRecursively(groupItems, configIndex + 1, level + 1, groupKey);

      if (childGroups.length > 0) {
        group.children = childGroups;
      } else {
        globalIndex += groupItems.length;
      }

      currentGroups.push(group);
    }

    return currentGroups;
  }

  const resultGroups = createGroupsRecursively(items, 0, 0);
  return { groups: resultGroups, groupedItems };
}
